import { useEffect, useState } from "preact/hooks";
import { defineMessages, useT } from "../i18n/index.ts";
import { Icon } from "../icons/Icon.tsx";

export interface HistoryTarget {
  /** Numéro de page (position Readium) de la destination, affiché sur le bouton comme dans l'app. */
  position?: number;
}

interface Props {
  position?: number;
  positionCount: number;
  progression?: number;
  /** A book read from right to left: the slider starts on the right. */
  rtl?: boolean;
  backward?: HistoryTarget;
  forward?: HistoryTarget;
  onSeek(position: number): void;
  onHistoryBackward(): void;
  onHistoryForward(): void;
}

const messages = defineMessages(
  {
    pageOf: "{position} sur {count}",
    percent: "{value} %",
    backToPage: "Revenir à la page {page}",
    back: "Revenir à la position précédente",
    forwardToPage: "Retourner à la page {page}",
    forward: "Retourner à la position suivante",
    progress: "Progression dans le livre",
  },
  {
    pageOf: "{position} of {count}",
    percent: "{value}%",
    backToPage: "Go back to page {page}",
    back: "Go back to the previous position",
    forwardToPage: "Go forward to page {page}",
    forward: "Go forward to the next position",
    progress: "Progress through the book",
  },
);

/**
 * Curseur de progression, « n sur N » et historique de navigation (retour à la page
 * quittée après un saut : table des matières, signet, curseur, lien), comme le pied de page de l'app.
 */
export function ReaderFooter(props: Props) {
  const { position, positionCount, progression, backward, forward, onSeek } = props;
  const t = useT(messages);
  // Valeur locale pendant le glissement, pour ne pas sauter de page à chaque pixel.
  const [dragging, setDragging] = useState<number | null>(null);
  useEffect(() => setDragging(null), [position]);
  const current = dragging ?? position ?? 1;
  const label =
    positionCount > 0
      ? t("pageOf", { position: current, count: positionCount })
      : progression !== undefined
        ? t("percent", { value: Math.round(progression * 100) })
        : "";

  return (
    <footer class="msb-footer">
      <div class="msb-footer__history msb-footer__history--back">
        {/* Toujours monté : retirer du DOM le bouton qui a le focus perturbe la transition de Readium. */}
        <button
          type="button"
          class="msb-history-button"
          hidden={!backward}
          onClick={props.onHistoryBackward}
          aria-label={backward?.position ? t("backToPage", { page: backward.position }) : t("back")}
        >
          <Icon name="undo" size={20} />
          {backward?.position !== undefined && <span>{backward.position}</span>}
        </button>
      </div>
      {positionCount > 1 && (
        <input
          class="msb-slider"
          dir={props.rtl ? "rtl" : undefined}
          type="range"
          min={1}
          max={positionCount}
          step={1}
          value={current}
          aria-label={t("progress")}
          aria-valuetext={label}
          style={{ "--msb-slider-fill": `${((current - 1) / (positionCount - 1)) * 100}%` }}
          onInput={(e) => setDragging(Number(e.currentTarget.value))}
          onChange={(e) => onSeek(Number(e.currentTarget.value))}
        />
      )}
      <span class="msb-footer__label">{label}</span>
      <div class="msb-footer__history msb-footer__history--forward">
        <button
          type="button"
          class="msb-history-button"
          hidden={!forward}
          onClick={props.onHistoryForward}
          aria-label={forward?.position ? t("forwardToPage", { page: forward.position }) : t("forward")}
        >
          {forward?.position !== undefined && <span>{forward.position}</span>}
          <Icon name="redo" size={20} />
        </button>
      </div>
    </footer>
  );
}
